import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Quote, BadgeCheck } from 'lucide-react';
import { SectionTitle } from './ChineseDecor';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';

interface LocalizedField { 'zh-HK'?: string; 'zh-CN'?: string; en?: string; }
interface Case {
  tag: string | LocalizedField;
  title: string | LocalizedField;
  person: string | LocalizedField;
  content: string | LocalizedField;
  source: string | LocalizedField;
}

interface CaseStudiesProps {
  content?: Record<string, unknown>;
}

const CaseStudies: React.FC<CaseStudiesProps> = ({ content = {} }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;

  const eyebrow = getLocalizedText(content.eyebrow, lang, t('cases.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('cases.title'));
  const intro = getLocalizedText(content.intro, lang, t('cases.intro'));
  const sourceLabel = getLocalizedText(content.sourceLabel, lang, t('cases.source_label'));

  const legacyCases = t('cases.list', { returnObjects: true }) as Case[];
  const cases = (content.list as Case[] | undefined) || legacyCases;

  return (
    <section id="cases" className="relative py-12 md:py-16 bg-xuan overflow-hidden">
      <div className="absolute inset-0 bg-cloud opacity-[0.5] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle eyebrow={eyebrow} title={title} />
        <p className="max-w-3xl mx-auto text-center mt-6 text-brand-ink/60 font-sans text-[13px] leading-relaxed">
          {intro}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {cases.map((c, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.1 }}
              className="relative bg-brand-paper border border-brand-ink/5 p-7 hover:border-brand-gold/40 hover:shadow-lg transition-all flex flex-col"
            >
              {/* Vermilion tag */}
              <span className="absolute -top-3 left-6 bg-brand-cinnabar text-brand-paper text-xs font-serif px-3 py-1 tracking-widest">
                {getLocalizedText(c.tag, lang, legacyCases?.[i]?.tag)}
              </span>
              <Quote className="w-8 h-8 text-brand-gold/25 mb-3 mt-2" />
              <h3 className="font-serif text-lg text-brand-ink mb-1">{getLocalizedText(c.title, lang, legacyCases?.[i]?.title)}</h3>
              <p className="text-[12px] font-sans text-brand-gold tracking-wide mb-4">{getLocalizedText(c.person, lang, legacyCases?.[i]?.person)}</p>
              <p className="text-brand-ink/70 text-[13px] font-sans leading-relaxed flex-1">{getLocalizedText(c.content, lang, legacyCases?.[i]?.content)}</p>
              <div className="mt-5 pt-4 border-t border-brand-ink/5 flex items-center gap-2 text-[11px] font-sans text-brand-ink/50">
                <BadgeCheck className="w-3.5 h-3.5 text-brand-gold" />
                <span>{sourceLabel}：{getLocalizedText(c.source, lang, legacyCases?.[i]?.source)}</span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
